/**
 * Providoor Concierge Availability Module
 * Rewritten version:
 * - Does not strip card display styles
 * - Hides cards/bars/FAB using a CSS class instead of inline display resets
 * - Preserves natural theme layout for collection/product grid cards
 * - Supports endpoint responses with either `available` or `active`
 */
(function() {
  'use strict';

  var ENDPOINT = window.__conciergeEndpoint || '';
  var PHONE = window.__conciergePhone || '';
  var POLL_INTERVAL = 30000;
  var state = { available: false };
  var HIDDEN_CLASS = 'concierge-is-hidden';
  var STYLE_ID = 'concierge-runtime-styles';

  function injectRuntimeStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = '.' + HIDDEN_CLASS + '{display:none !important;}';
    document.head.appendChild(style);
  }

  function getRegularBar() {
    return document.querySelector('[data-announcement-regular]');
  }

  function getConciergeBar() {
    return document.querySelector('[data-concierge-bar]');
  }

  function getConciergeCards() {
    return document.querySelectorAll('[data-concierge-card], .concierge-card');
  }

  function getCardButtons() {
    return document.querySelectorAll('[data-concierge-card-btn]');
  }

  function getFab() {
    return document.getElementById('concierge-fab');
  }

  function addHidden(el) {
    if (!el) return;
    el.classList.add(HIDDEN_CLASS);
    el.setAttribute('aria-hidden', 'true');
  }

  function removeHidden(el) {
    if (!el) return;
    el.classList.remove(HIDDEN_CLASS);
    el.removeAttribute('aria-hidden');
  }

  function setVisible(el, shouldShow) {
    if (!el) return;
    if (shouldShow) {
      removeHidden(el);
    } else {
      addHidden(el);
    }
  }

  function setNodeListVisible(nodeList, shouldShow) {
    if (!nodeList || !nodeList.length) return;

    nodeList.forEach(function(el) {
      setVisible(el, shouldShow);
    });
  }

  function normaliseAvailability(data) {
    if (!data || typeof data !== 'object') return false;
    if (typeof data.available !== 'undefined') return data.available === true;
    if (typeof data.active !== 'undefined') return data.active === true;
    return false;
  }

  function updateAnnouncementBars(isAvailable) {
    var regularBar = getRegularBar();
    var conciergeBar = getConciergeBar();
    if (!conciergeBar) return;
    [regularBar, conciergeBar].forEach(function(bar, index) {
      if (!bar) return;
      var active = index === 1 ? isAvailable : !isAvailable;
      removeHidden(bar);
      bar.style.display = '';
      bar.classList.toggle('concierge-announcement-is-inactive', !active);
      if (active) {
        bar.removeAttribute('aria-hidden');
        bar.removeAttribute('inert');
      } else {
        bar.setAttribute('aria-hidden', 'true');
        bar.setAttribute('inert', '');
      }
    });
  }

  function updateConciergeCards(isAvailable, isLoading) {
    var cards = getConciergeCards();
    var cardBtns = getCardButtons();

    setNodeListVisible(cards, true);

    cardBtns.forEach(function(btn) {
      var disabled = !isAvailable || !PHONE;
      btn.classList.toggle('concierge-card--offline', !isAvailable);
      var label = btn.getAttribute('data-concierge-' + (isLoading ? 'checking' : isAvailable ? 'online' : 'offline')) || (isLoading ? 'Checking availability' : isAvailable ? 'Concierge Online' : 'Concierge Offline');

      if (!disabled) {
        btn.setAttribute('href', 'tel:' + PHONE);
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('is-disabled');
      } else {
        btn.removeAttribute('href');
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is-disabled');
      }

      var cta = btn.querySelector('[data-concierge-card-cta]');
      if (cta) {
        var ctaLabel = isAvailable && PHONE ? btn.getAttribute('data-concierge-call') || '\u260E Call Now' : label;
        if (cta.textContent !== ctaLabel) cta.textContent = ctaLabel;
      }

      var status = btn.querySelector('[data-concierge-card-status]');
      var badge = btn.querySelector('[data-concierge-card-badge]');
      if (status) {
        if (status.textContent !== label) status.textContent = label;
      } else if (badge) {
        var updated = false;
        badge.childNodes.forEach(function(node) {
          if (node.nodeType === Node.TEXT_NODE) {
            node.textContent = updated ? '' : ' ' + label;
            updated = true;
          }
        });
        if (!updated) badge.appendChild(document.createTextNode(' ' + label));
      }
    });
  }

  function updateFab(isAvailable) {
    var fab = getFab();
    if (!fab) return;

    fab.classList.toggle('concierge-fab--online', isAvailable);
    fab.classList.toggle('concierge-fab--offline', !isAvailable);

    setVisible(fab, isAvailable);

    if (isAvailable && PHONE) {
      fab.setAttribute('href', 'tel:' + PHONE);
      fab.style.pointerEvents = '';
      fab.style.opacity = '1';
      fab.style.visibility = 'visible';
      fab.removeAttribute('aria-disabled');
    } else {
      fab.removeAttribute('href');
      fab.style.pointerEvents = 'none';
      fab.style.opacity = '0';
      fab.style.visibility = 'hidden';
      fab.setAttribute('aria-disabled', 'true');
    }
  }

  function updateUI(data) {
    state = data || state;
    var isAvailable = !!state.available;

    updateAnnouncementBars(isAvailable);
    updateConciergeCards(isAvailable);
    updateFab(isAvailable);

    if (typeof gtag === 'function') {
      gtag('event', isAvailable ? 'concierge_banner_view' : 'concierge_offline_view');
    }
  }

  function buildStatusUrl() {
    if (!ENDPOINT || ENDPOINT.indexOf('http') !== 0) return '';
    return ENDPOINT + (ENDPOINT.indexOf('?') >= 0 ? '&' : '?') + '_=' + Date.now();
  }

  function checkStatus() {
    // Endpoint-driven: visibility follows LIVE Aircall agent availability.
    // window.__conciergeEndpoint -> Google Apps Script ?mode=status returns
    // {"available":true}. Simple GET (no custom headers) so no CORS preflight;
    // the endpoint sends Access-Control-Allow-Origin:*. (Ryan 2026-07-24)
    var url = buildStatusUrl();
    if (!url) {
      updateUI({ available: false });
      return;
    }
    fetch(url, { method: 'GET', cache: 'no-store' })
      .then(function(res) { return res.ok ? res.json() : null; })
      .then(function(data) { updateUI({ available: normaliseAvailability(data) }); })
      .catch(function() { updateUI({ available: false }); });
  }

  function trackClicks() {
    document.addEventListener('click', function(e) {
      if (typeof gtag !== 'function') return;

      if (e.target.closest('[data-concierge-phone]')) {
        gtag('event', 'concierge_banner_click');
      }

      var cardButton = e.target.closest('[data-concierge-card-btn]');
      if (cardButton && !cardButton.hasAttribute('aria-disabled')) {
        gtag('event', 'concierge_menu_prompt_click');
      }

      if (e.target.closest('#concierge-fab')) {
        gtag('event', 'concierge_fab_click');
      }
    });
  }

  function init() {
    injectRuntimeStyles();
    trackClicks();

    var conciergeBar = getConciergeBar();
    var conciergeCards = getConciergeCards();
    var fab = getFab();

    // If this page/theme does not use concierge bar/cards,
    // leave existing announcement UI alone.
    if (!conciergeBar && !conciergeCards.length && !fab) {
      return;
    }

    updateAnnouncementBars(false);
    if (fab) updateFab(false);
    if (conciergeCards.length) updateConciergeCards(false, true);

    checkStatus();
    window.setInterval(checkStatus, POLL_INTERVAL);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();